import { useState } from "react";
import { hivesApi } from "../../services/hivesApi";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";

/**
 * Close a yard's worth of hives for the season (#389).
 *
 * THE GESTURE THIS EXISTS FOR: a truck is loaded and leaves for Florida,
 * or for the pecan groves, and fifty hives are shut at once. Reported
 * from a device as the reason the account-level switch was wrong --
 * "about 50% of the hives" go, and the rest keep working.
 *
 * A FORM, NOT A CONFIRMATION. It has a field, and the dialog standard's
 * first rule is that anything with a field is a form. Asking "are you
 * sure?" and also taking a date is how the date ends up optional by
 * accident.
 *
 * ONE REQUEST for the whole selection, because POST /hives/season takes a
 * batch. Fifty separate calls would be fifty chances to leave a yard in
 * two states, which is the thing a batch exists to prevent.
 */
export function BatchSeasonDialog({ open, onClose, hiveIds, onApplied }) {
  const { notify } = useToast();
  const [closedUntil, setClosedUntil] = useState("");
  const [busy, setBusy] = useState(false);

  // Local midnight, not UTC. toISOString() converts first, so west of
  // Greenwich an evening `min` lands on yesterday and the picker offers a
  // date the server will treat as already expired.
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString().slice(0, 10);

  async function apply() {
    setBusy(true);
    try {
      await hivesApi.setSeason(hiveIds, closedUntil);
      notify(
        `${hiveIds.length} hive${hiveIds.length === 1 ? "" : "s"} closed until ` +
        new Date(closedUntil).toLocaleDateString(),
        "success",
      );
      onApplied?.();
      onClose();
    } catch (err) {
      notify(err.apiMessage || "Could not close these hives for the season", "danger");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Close ${hiveIds.length} hive${hiveIds.length === 1 ? "" : "s"} for the season`}
      footer={
        <Button disabled={busy || !closedUntil} onClick={apply}>
          {busy ? "Closing…" : "Close These Hives"}
        </Button>
      }
    >
      <p style={{ marginTop: 0 }}>
        While they are closed these hives are checked on your closed-season schedule
        instead of the ordinary one, so they stop being flagged as overdue every day.
      </p>
      {/* Closed is not silent, and saying so is the difference between a
          longer schedule and what somebody might reasonably assume is a
          mute. Winter still has hefting, mouse guards and ventilation. */}
      <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
        A longer schedule, not a pause — and they open again by themselves on the date
        you choose, so there is nothing to remember in spring.
      </p>
      <label>
        Closed until
        <input id="batch-season-dialog-closed-until" name="closed-until"
          type="date"
          /* A past date would do nothing at all and read as a broken save. */
          min={today}
          value={closedUntil}
          onChange={(e) => setClosedUntil(e.target.value)}
          className="touch-target"
        />
      </label>
    </Modal>
  );
}
